import {CardlessDetectionSetting} from "@/core/interfaces/detection/detection";

/**
 * Commands the Connect can execute remotely.
 */
export enum RemoteCommand {
    DETECTION_CAPTURE = 'detection.capture',
    DETECTION_CALIBRATE = 'detection.calibrate',
    DETECTION_CHECK_READER = 'detection.checkReader',
    CPS_IDENTITY = 'cps.identity',
}

/**
 * Broadcast event received on the pharmacy channel when the Admin sends a remote command.
 */
export interface RemoteCommandEvent {
    /** Unique id of the command request, used to correlate the uploaded result. */
    id: string;
    /** The command to execute. */
    command: RemoteCommand | string;
    /** Target host name; when set, only the matching Connect device answers. */
    hostname?: string | null;
    /** Command-specific parameters. */
    params?: Record<string, unknown>;
}

/**
 * Options accepted by the `detection:capture` IPC handler.
 */
export interface DetectionCaptureOptions {
    /** Overrides the LGO window title regex, so the web can adapt without an Electron release. */
    windowRegex?: string;
    /** Whether to run the OCR field parsing on the capture. */
    parse?: boolean;
    /** Image renditions to return, all taken from the same capture. */
    formats?: ('png' | 'gray' | 'raw')[];
}

/**
 * Failed capture returned by the `detection:capture` IPC handler.
 */
export interface DetectionCaptureError {
    error: string;
}

/**
 * Successful capture returned by the `detection:capture` IPC handler.
 */
export interface DetectionCaptureSuccess {
    /** PNG bytes of the full LGO window screenshot (format `png`). */
    png?: Uint8Array;
    /** Grayscale rendition (format `gray`), as analyzed by the calibrators. */
    gray?: {data: Uint8Array; width: number; height: number};
    /** Raw RGBA rendition (format `raw`). */
    raw?: {data: Uint8Array; width: number; height: number; channels: number};
    /** OCR text parsed for each configured field (option `parse`). */
    patient?: Record<string, string | number | null>;
    /** Effective crop configuration. */
    config: CardlessDetectionSetting[];
    /** Capture context. */
    meta: {
        lgoId: number;
        screen: {
            scaleFactor: number;
            workAreaSize: {width: number; height: number};
            size: {width: number; height: number};
        };
        margins: {top: number; left: number};
        connectVersion: string;
        capturedAt: string;
    };
}

export type DetectionCaptureIpcResult = DetectionCaptureSuccess | DetectionCaptureError;

/**
 * Result of a remote command execution, whispered back on the channel
 * (client event) so the Admin receives it without any server-side endpoint.
 */
export interface RemoteCommandResultPayload {
    /** The id of the command request being answered. */
    id: string;
    hostname: string;
    command: string;
    status: 'success' | 'error';
    error?: string;
    /** Command-specific result data. */
    data?: Record<string, unknown>;
}
